// App
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { paths } from "utils/constants";

// Components
import Home from "./Home/Home";
import SavePredictions from "components/AdminDashboard/SavePredictions/SavePredictions";
import Login from "components/Login/Login";
import ProtectedRoute from "components/ProtectedRoute";
import SymbolsManagement from "components/SymbolsManagement/SymbolsManagement";
import AdminDashboard from "components/AdminDashboard/AdminDashboard";
import ModelCreation from "components/ModelCreation/ModelCreation";
import UsersManagement from "components/AdminDashboard/UsersManagement/UsersManagement";
import SystemControl from "components/AdminDashboard/SystemControl/SystemControl";
import Trading from "components/Trading/Trading";
import UserSettings from "components/UserSettings/UserSettings";
import AiMagician from "components/AiMagician/AiMagician";
import StrategyPreview from "components/StrategyPreview/StrategyPreview";
import Register from "components/Register/Register";
import RecentTasks from "./AdminDashboard/RecentTasks/RecentTasks";
import Splits from "./AdminDashboard/Splits/Splits";
import AdminFillQuality from "./AdminDashboard/FillQuality/AdminFillQuality";
import Automation from "./Automation/Automation";
import Agent from "components/Agent/Agent";
import OptionSimulator from "components/OptionSimulator/OptionSimulator";
import AiOptionsAnalysis from "components/AiOptionsAnalysis/AiOptionsAnalysis";
import OptionsLeaderboard from "components/OptionsLeaderboard/OptionsLeaderboard";
import StrategiesExplorer from "components/StrategiesExplorer/StrategiesExplorer";

// The old Strategies / Open Positions / Trades History / Options Strategies
// pages are tabs of the Trading page now; keeps the old link's query
// (e.g. ?strategyId=) and adds the tab/instrument to open.
const RedirectToTrading = ({ params }) => {
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  Object.entries(params).forEach(([key, value]) => search.set(key, value));
  return <Navigate to={`${paths.trading}?${search}`} replace />;
};

const tradingRedirects = [
  [paths.strategies, { tab: "strategies" }],
  [paths.openPositions, { tab: "positions" }],
  [paths.tradesHistory, { tab: "history" }],
  [paths.optionsStrategies, { instrument: "options", tab: "strategies" }],
  [paths.watchlist, { instrument: "options", tab: "strategies" }],
];

const RouterConfig = () => {
  return (
    <Routes>
      <Route path={paths.login} element={<Login />} />
      <Route path={paths.register} element={<Register />} />
      <Route
        path={paths.home}
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />

      <Route
        path={paths.ai}
        element={
          <ProtectedRoute>
            <AiMagician />
          </ProtectedRoute>
        }
      />

      <Route
        path={paths.aiNew}
        element={
          <ProtectedRoute>
            <AiMagician />
          </ProtectedRoute>
        }
      />

      <Route
        path={paths.optionsAnalysis}
        element={
          <ProtectedRoute>
            <AiOptionsAnalysis />
          </ProtectedRoute>
        }
      />

      <Route
        path={paths.strategiesExplorer}
        element={
          <ProtectedRoute>
            <StrategiesExplorer />
          </ProtectedRoute>
        }
      />

      <Route
        path={paths.optionsLeaderboard}
        element={
          <ProtectedRoute>
            <OptionsLeaderboard />
          </ProtectedRoute>
        }
      />

      <Route
        path={paths.trading}
        element={
          <ProtectedRoute>
            <Trading />
          </ProtectedRoute>
        }
      />
      {tradingRedirects.map(([path, params]) => (
        <Route
          key={path}
          path={path}
          element={<RedirectToTrading params={params} />}
        />
      ))}

      <Route
        path={paths.automation}
        element={
          <ProtectedRoute>
            <Automation />
          </ProtectedRoute>
        }
      />

      <Route path={paths.strategy} element={<StrategyPreview />} />

      <Route
        path={paths.createModel}
        element={
          <ProtectedRoute requiredPermissions={["manager"]}>
            <ModelCreation />
          </ProtectedRoute>
        }
      />

      <Route
        path={paths.userSettings}
        element={
          <ProtectedRoute>
            <UserSettings />
          </ProtectedRoute>
        }
      />

      <Route
        path={paths.agent}
        element={
          <ProtectedRoute requiredPermissions={["manager"]}>
            <Agent />
          </ProtectedRoute>
        }
      />

      <Route
        path={paths.optionSimulator}
        element={
          <ProtectedRoute requiredPermissions={["manager"]}>
            <OptionSimulator />
          </ProtectedRoute>
        }
      />

      <Route
        path={`${paths.optionSimulator}/:symbol`}
        element={
          <ProtectedRoute requiredPermissions={["manager"]}>
            <OptionSimulator />
          </ProtectedRoute>
        }
      />

      <Route
        path={`${paths.optionSimulator}/:symbol/:expiration`}
        element={
          <ProtectedRoute requiredPermissions={["manager"]}>
            <OptionSimulator />
          </ProtectedRoute>
        }
      />

      <Route
        path={paths.admin}
        element={
          <ProtectedRoute requiredPermissions={["manager"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      >
        <Route path={paths.usersManagement} element={<UsersManagement />} />
        <Route
          path={`${paths.usersManagement}/:id`}
          element={<UsersManagement />}
        />
        <Route path={paths.systemControl} element={<SystemControl />} />
        <Route path={paths.symbolsManagement} element={<SymbolsManagement />} />
        <Route path={paths.savePredictions} element={<SavePredictions />} />
        <Route path={paths.recentTasks} element={<RecentTasks />} />
        <Route path={paths.splits} element={<Splits />} />
        <Route path={paths.fillQuality} element={<AdminFillQuality />} />
      </Route>

      {/* Redirect to home on unknown paths */}
      <Route path="*" element={<Navigate to={paths.home} />} />
    </Routes>
  );
};

export default RouterConfig;
